import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { BarChart3, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SRC_COLORS = { 'Guest Visit': '#f59e0b', Internet: '#38bdf8', 'Phone-in': '#84cc16', Referral: '#1d4ed8', 'Walk In': '#ef4444', Other: '#94a3b8', Unmatched: '#cbd5e1' };
const SRC_ORDER = ['Guest Visit', 'Internet', 'Phone-in', 'Referral', 'Walk In', 'Other', 'Unmatched'];

const Tile = ({ label, value, sub, tone }) => (
  <div className={`rounded-xl border px-4 py-3 ${tone}`} data-testid={`club-tile-${label.replace(/\s+/g, '-').toLowerCase()}`}>
    <p className="text-xs font-semibold text-slate-600">{label}</p>
    <p className="text-3xl font-bold text-slate-900 mt-1">{value}<span className="text-lg">%</span></p>
    <p className="text-[11px] text-slate-500 mt-0.5">{sub}</p>
  </div>
);

const NewCallersChart = ({ daily, sourceTotals, gsOn }) => {
  const days = daily || [];
  if (days.length === 0) return <p className="text-sm text-slate-400">No new callers this month yet.</p>;
  const W = Math.max(560, days.length * 34), H = 180, PAD = 24;
  const maxV = Math.max(1, ...days.map((d) => Object.values(d.sources).reduce((a, b) => a + b, 0)));
  let svg = '';
  days.forEach((d, i) => {
    const x = PAD + i * 34;
    let yCursor = H - 18;
    const total = Object.values(d.sources).reduce((a, b) => a + b, 0);
    SRC_ORDER.forEach((s) => {
      const v = d.sources[s] || 0;
      if (!v) return;
      const h = (v / maxV) * (H - 50);
      yCursor -= h;
      svg += `<rect x="${x}" y="${yCursor}" width="20" height="${h}" fill="${SRC_COLORS[s]}"><title>${d.date} ${s}: ${v}</title></rect>`;
    });
    svg += `<text x="${x + 10}" y="${yCursor - 4}" font-size="10" font-weight="bold" fill="#334155" text-anchor="middle">${total}</text>`;
    svg += `<text x="${x + 10}" y="${H - 4}" font-size="8" fill="#94a3b8" text-anchor="middle">${d.date.slice(8)}</text>`;
  });
  return (
    <div className="overflow-x-auto" data-testid="club-new-callers-chart">
      <svg width={W} height={H} dangerouslySetInnerHTML={{ __html: svg }} />
      <div className="flex items-center gap-3 flex-wrap mt-1">
        {SRC_ORDER.filter((s) => sourceTotals[s]).map((s) => (
          <span key={s} className="text-xs text-slate-600 inline-flex items-center gap-1">
            <span className="w-3 h-3 rounded-sm inline-block" style={{ background: SRC_COLORS[s] }} /> {!gsOn && s === 'Unmatched' ? 'New' : s} <b>{sourceTotals[s]}</b>
          </span>
        ))}
      </div>
    </div>
  );
};

export default function ClubSummaryPage() {
  const { user } = useAuth();
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [agentId, setAgentId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/analytics/club-summary`, { params: { month, ...(agentId ? { agent_id: agentId } : {}) } })
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [month, agentId]);
  useEffect(() => { load(); }, [load]);

  const shiftMonth = (d) => {
    const [y, m] = month.split('-').map(Number);
    const nd = new Date(y, m - 1 + d, 1);
    setMonth(`${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, '0')}`);
  };

  const t = data?.tiles;
  const gsOn = !!data?.gymsales_enabled;
  const title = gsOn ? 'Club Summary' : 'Site Summary';
  const mix = data?.contact_mix?.totals;
  const gsDays = data?.gymsales_daily || [];
  const gsTot = gsDays.reduce((a, g) => ({ calls: a.calls + (g.calls || 0), spoke_to: a.spoke_to + (g.spoke_to || 0), no_answer: a.no_answer + (g.no_answer || 0) }), { calls: 0, spoke_to: 0, no_answer: 0 });

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="club-summary-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
            <BarChart3 className="w-6 h-6 text-teal-600" /> {title}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">{gsOn ? 'GymSales-style monthly report, built from your call transcripts and gradings.' : 'Monthly performance report, built from your call transcripts and gradings.'}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => shiftMonth(-1)} className="p-1.5 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700" data-testid="club-month-prev"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm font-bold text-slate-800 w-20 text-center" data-testid="club-month-label">{month}</span>
          <button onClick={() => shiftMonth(1)} className="p-1.5 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700" data-testid="club-month-next"><ChevronRight className="w-4 h-4" /></button>
          <select value={agentId} onChange={(e) => setAgentId(e.target.value)}
            className="text-xs font-semibold border border-slate-200 rounded-md px-2 py-1.5 bg-white text-slate-700" data-testid="club-agent-select">
            <option value="">All staff</option>
            {(data?.agents || []).map((a) => <option key={a.agent_id} value={a.agent_id}>{a.agent_name || a.agent_id}</option>)}
          </select>
          <button onClick={() => window.print()} className="text-xs font-semibold px-3 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-white" data-testid="club-print">Download PDF</button>
        </div>
      </div>

      {loading || !data ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-teal-500" /></div>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Tile label="Contacts to Appointments" value={t.contacts_to_appointments_pct} sub={`${t.appointments} appointments detected from ${mix.spoke_to} conversations`} tone="border-rose-200 bg-rose-50" />
            <Tile label="Close Ratio" value={t.close_ratio_pct} sub={`${t.closed} closed of ${t.outcomes} graded deal outcomes`} tone="border-emerald-200 bg-emerald-50" />
            <Tile label="Revival Rate" value={t.revival_rate_pct} sub={`${t.revived} of ${t.revivable} revivable deals later converted`} tone="border-teal-200 bg-teal-50" />
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="club-contact-mix">
            <p className="font-semibold text-slate-900 mb-2">{gsOn ? 'Contact mix — VoiceFabric vs GymSales' : 'Contact mix'}</p>
            <div className="grid grid-cols-[1fr_repeat(4,90px)] text-xs font-bold text-slate-400 uppercase border-b border-slate-100 pb-1.5">
              <span /><span className="text-right">Phone-in</span><span className="text-right">Calls out</span><span className="text-right">Spoke to</span><span className="text-right">No answer</span>
            </div>
            <div className="grid grid-cols-[1fr_repeat(4,90px)] text-sm py-1.5 border-b border-slate-50">
              <span className="font-semibold text-slate-800">VoiceFabric (transcripts)</span>
              <span className="text-right tabular-nums text-slate-700">{mix.phone_in}</span>
              <span className="text-right tabular-nums text-slate-700">{mix.calls_out}</span>
              <span className="text-right tabular-nums text-emerald-700 font-semibold">{mix.spoke_to}</span>
              <span className="text-right tabular-nums text-slate-500">{mix.no_answer}</span>
            </div>
            {gsOn && (
            <div className="grid grid-cols-[1fr_repeat(4,90px)] text-sm py-1.5">
              <span className="font-semibold text-slate-800">GymSales (logged)</span>
              <span className="text-right tabular-nums text-slate-400">—</span>
              <span className="text-right tabular-nums text-slate-700">{gsTot.calls || '—'}</span>
              <span className="text-right tabular-nums text-slate-700">{gsTot.spoke_to || '—'}</span>
              <span className="text-right tabular-nums text-slate-700">{gsTot.no_answer || '—'}</span>
            </div>
            )}
            {gsOn && gsDays.length === 0 && (
              <p className="text-[11px] text-slate-400 mt-1" data-testid="club-gs-hint">GymSales side fills automatically once the 6am morning sweep can run (needs GET /people/ allowlisted on your gs-router). Discrepancies between the rows = calls not logged in one system.</p>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <p className="font-semibold text-slate-900 mb-1">New callers <span className="text-sm font-normal text-slate-400">· {data.new_callers.total} this month{gsOn ? ' · by GymSales source' : ''}</span></p>
            {gsOn && data.new_callers.pending_source_lookups > 0 && (
              <p className="text-[11px] text-slate-400 mb-1" data-testid="club-pending-sources">{data.new_callers.pending_source_lookups} callers not yet matched to a GymSales source — matching runs with the 6am morning sweep.</p>
            )}
            <NewCallersChart daily={data.new_callers.daily} sourceTotals={data.new_callers.source_totals} gsOn={gsOn} />
          </div>
        </div>
      )}
    </div>
  );
}
